/* eslint-disable @typescript-eslint/restrict-plus-operands */
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { ThemePalette } from 'mui.theme';
import { useAppSelector } from 'store/hook';
import { filterDtcByWords, mergeDtcEvents, cleanDTCType } from '../utils';
import TpmsInfo from './Tpms';
import { StyledTableHeaderCell } from 'views/TirePressure/Components/NotificationTable.styles';
import type { Asset } from 'types/asset.types';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { convertUtcToDisplayTime } from 'utils/helpers/dateTime';
import { PNG } from 'components/Asset/PNG';
import { getImageNameByMakeModelAndAsset } from 'utils/helpers/mapAssetImage';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface Props {
  enableDarkTheme: boolean;
  customerCode: string;
  unitSystem: string;
  data: any[];
  tpmsNotifications: any[];
  assets: Asset[];
}

export const MachineHealthWidget = ({
  enableDarkTheme,
  data,
  tpmsNotifications,
  assets,
}: Props) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: customerId } = useAppSelector(
    (state) => state.persistedReducer
  ).customer;
  const tirePatterns = useAppSelector((state) => state.tirePatternReducer);

  const makeModelDetails = useAppSelector(
    (state) => state.makeModelDetailsReducer
  );
  const { preferredTimeDisplayMode, timezone } = useTimeDisplayMode();
  const fontColor = palette.textPrimary;

  const allActiveDtc = data.filter((item) => item.is_active);

  const utilizationDtcEvents = filterDtcByWords(
    allActiveDtc,
    ['rpm'],
    ['dtc_type']
  );

  const productionDtcEvents = filterDtcByWords(
    allActiveDtc,
    ['payload', 'fuel', 'rpm', 'speed'],
    ['dtc_type']
  );

  const fuelDtcEvents = filterDtcByWords(allActiveDtc, ['fuel'], ['dtc_type']);
  let allDtcEvents = mergeDtcEvents(
    utilizationDtcEvents,
    productionDtcEvents,
    fuelDtcEvents
  );
  if (allDtcEvents.length > 0) {
    const now = new Date();
    const twoDaysAgo = new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000);

    allDtcEvents = allDtcEvents
      .map((item) => {
        const itemAsset = assets.find(
          (asset) => asset.bumperNumber === item.asset
        );
        if (itemAsset) {
          return {
            ...item,
            make: itemAsset.make,
            model: itemAsset.model,
            assetType: itemAsset.assetType,
          };
        }
        return item;
      })
      .filter((item) => {
        const firstOccurrence = new Date(item.first_occurrence + 'Z');
        return firstOccurrence >= twoDaysAgo;
      });
  }

  return (
    <Box
      sx={{
        border: '1px solid #2F445E',
        borderRadius: '4px',
        minHeight: '300px',
        overflow: 'scroll',
        color: fontColor,
        padding: '20px',
        marginTop: '20px',
      }}
    >
      <Box
        sx={{
          minHeight: '280px',
          color: palette.textPrimary,
        }}
      >
        <Grid container spacing={2} sx={{ height: '100%' }}>
          <Grid item lg={8} sx={{ height: '100%' }}>
            <Box
              sx={{
                height: '100%',
                border: '1px solid #2F445E',
                borderRadius: '4px',
                backgroundColor: palette.paperBackground,
                padding: '10px',
              }}
            >
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: ThemePalette.typography.grey,
                }}
              >
                DTC
              </Typography>
              <Box
                sx={{
                  marginTop: '10px',
                }}
              >
                {allDtcEvents.length === 0 ? (
                  <Typography
                    sx={{
                      color: fontColor,
                      marginTop: '10px',
                      fontSize: '12px',
                    }}
                  >
                    {' '}
                    {t('no_dtc_events')}
                  </Typography>
                ) : (
                  <TableContainer
                    sx={{
                      border: '1px solid #2F445E',
                      borderRadius: '4px',
                      maxHeight: '250px',
                      overflow: 'scroll',
                    }}
                  >
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <StyledTableHeaderCell
                            enabledarktheme={enableDarkTheme}
                          >
                            {t('asset')}
                          </StyledTableHeaderCell>
                          <StyledTableHeaderCell
                            enabledarktheme={enableDarkTheme}
                          >
                            {t('grade_importer_section_details')}
                          </StyledTableHeaderCell>
                          <StyledTableHeaderCell
                            enabledarktheme={enableDarkTheme}
                          >
                            {t('status')}
                          </StyledTableHeaderCell>
                          <StyledTableHeaderCell
                            enabledarktheme={enableDarkTheme}
                          >
                            {t('start_time_2')}
                          </StyledTableHeaderCell>
                          <StyledTableHeaderCell
                            enabledarktheme={enableDarkTheme}
                          >
                            Impact
                          </StyledTableHeaderCell>
                          <StyledTableHeaderCell
                            enabledarktheme={enableDarkTheme}
                          />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {allDtcEvents.map((row: any, index: number) => (
                          <TableRow
                            key={index}
                            sx={{
                              '& td': {
                                border: 'none',
                                borderBottom:
                                  index !== allDtcEvents.length - 1
                                    ? '1px solid #2F445E'
                                    : 'none',
                              },
                            }}
                          >
                            <TableCell>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 40,
                                    height: 40,
                                    bgcolor: palette.toolBarBackground,
                                    borderRadius: '83px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <PNG
                                    name={getImageNameByMakeModelAndAsset(
                                      row.make,
                                      row.model,
                                      row.asset_type,
                                      'Small'
                                    )}
                                    alt={t('assetimage')}
                                    style={{
                                      height: '35px',
                                      width: '100%',
                                    }}
                                  />
                                </Box>
                                <Box>
                                  <Typography
                                    sx={{
                                      fontSize: '12px',
                                      color: fontColor,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'left',
                                      }}
                                    >
                                      {row.asset}
                                    </Box>
                                  </Typography>
                                  <Typography
                                    sx={{ fontSize: '12px', color: fontColor }}
                                  >
                                    {`${row?.make}-${row?.model}`}
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography
                                sx={{
                                  fontSize: '12px',
                                  color: fontColor,
                                }}
                              >
                                <span
                                  style={{
                                    fontStyle: 'italic',
                                    textDecoration: 'underline dotted',
                                  }}
                                >
                                  {cleanDTCType(row.dtc_type)}
                                </span>{' '}
                                {t('with_fault_code')}{' '}
                                <span
                                  style={{
                                    fontWeight: 'bold',
                                    textDecoration: 'underline dotted',
                                  }}
                                >
                                  {row.dtc_code}
                                </span>{' '}
                                -{' '}
                                <span
                                  style={{
                                    fontStyle: 'italic',
                                    textDecoration: 'underline dotted',
                                  }}
                                >
                                  {row.dtc_description}
                                </span>
                                .
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Typography
                                sx={{
                                  fontSize: '12px',
                                  color: fontColor,
                                  backgroundColor: row.is_active
                                    ? '#EB5757'
                                    : '#009161',
                                  borderRadius: '4px',
                                  padding: '5px 10px 5px 10px',
                                }}
                              >
                                {row.is_active ? 'Active' : 'Inactive'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                sx={{
                                  fontSize: '12px',
                                  color: fontColor,
                                }}
                              >
                                {convertUtcToDisplayTime(
                                  row.first_occurrence,
                                  preferredTimeDisplayMode,
                                  timezone
                                )}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                sx={{
                                  fontSize: '12px',
                                  color: fontColor,
                                }}
                              >
                                {row.dtcMetric}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Button
                                size="small"
                                sx={{
                                  padding: '4px',
                                  fontSize: '12px',
                                  color: palette.textPrimary,
                                  border: `1px solid ${palette.primary}`,
                                  textTransform: 'none',
                                }}
                                color={enableDarkTheme ? 'primary' : 'info'}
                                onClick={(e) => {
                                  const sensorFilter =
                                    row.impact === 'Production'
                                      ? ['payload', 'fuel', 'rpm', 'speed']
                                      : row.impact === 'Utilization'
                                      ? ['rpm']
                                      : ['fuel'];
                                  e.preventDefault();
                                  navigate('/diagnostic-trouble-codes', {
                                    state: {
                                      data: {
                                        isActive: true,
                                        sensorFilter,
                                      },
                                    },
                                  });
                                }}
                                variant="contained"
                              >
                                {'Details'}
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            </Box>
          </Grid>
          <Grid item lg={4} sx={{ height: '100%' }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                gap: 2,
              }}
            >
              <TpmsInfo
                enableDarkTheme={enableDarkTheme}
                data={tpmsNotifications}
                makeModelDetails={makeModelDetails}
                tirePatterns={tirePatterns}
                customerId={customerId}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};
